import { forwardRef, useImperativeHandle, useMemo, useRef, type ForwardedRef, type ReactNode, type RefObject, type PropsWithChildren } from 'react';
import { Canvas, useFrame, type ThreeEvent } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import type { TwinScene as TwinSceneData, TwinAsset, TwinIssue } from '@cdf/api-client-react';
import {
  projectLngLat,
  projectPolygon,
  projectLineString,
  STATUS_COLORS,
  ASSET_TYPE_COLORS,
  type GeoJsonPolygon,
  type GeoJsonLineString
} from '@/lib/geo-projection';
import { checkWebGLCapability } from '@/lib/webgl-capability';
import type { PanDirection } from '@/components/ui/zoom-pan-controls';
import { ListIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

// ── Types ─────────────────────────────────────────────────────────────────

export interface SelectedItem {
  kind: 'asset' | 'issue';
  id: string;
  data: TwinAsset | TwinIssue;
}

interface TwinSceneProps extends PropsWithChildren {
  data: TwinSceneData;
  onSelect: (item: SelectedItem | null) => void;
  selectedId: string | null;
  showBoundary?: boolean;
  showBuildings?: boolean;
  showAssets?: boolean;
  showIssues?: boolean;
}

export interface TwinCameraController {
  zoomIn: () => void;
  zoomOut: () => void;
  pan: (direction: PanDirection) => void;
  reset: () => void;
}

// ── Helper: create extruded building geometry ─────────────────────────────

function createBuildingGeometry(
  footprint: { [key: string]: unknown },
  height: number,
  centerLng: number,
  centerLat: number
): THREE.BufferGeometry {
  const polygon = footprint as unknown as GeoJsonPolygon;
  const projected = projectPolygon(polygon, centerLng, centerLat);
  const shape = new THREE.Shape();
  projected.forEach(([x, z], i) => {
    if (i === 0) shape.moveTo(x, z);
    else shape.lineTo(x, z);
  });
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: height,
    bevelEnabled: false
  });
  geo.rotateX(Math.PI / 2);
  return geo;
}

// ── Region ground ─────────────────────────────────────────────────────────

function RegionGround({ data }: { data: TwinSceneData }) {
  const geometry = useMemo(() => {
    const boundary = data.region.boundary as unknown as GeoJsonPolygon;
    const projected = projectPolygon(boundary, data.region.centerLng, data.region.centerLat);
    const shape = new THREE.Shape();
    projected.forEach(([x, z], i) => {
      if (i === 0) shape.moveTo(x, z);
      else shape.lineTo(x, z);
    });
    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: 0.5,
      bevelEnabled: false
    });
    geo.rotateX(Math.PI / 2);
    return geo;
  }, [data]);

  return (
    <mesh geometry={geometry} position={[0, -0.25, 0]} receiveShadow>
      <meshStandardMaterial
        color={data.region.tenantBrandingColor ?? '#10b981'}
        transparent
        opacity={0.3}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

// ── Buildings ─────────────────────────────────────────────────────────────

const BUILDING_COLORS: Record<string, string> = {
  residential: '#d4d4d8',
  commercial: '#a1a1aa',
  mixed_use: '#b8b8c0',
  civic: '#9ca3af'
};

function Buildings({ data }: { data: TwinSceneData }) {
  const { geometries, buildingTypes } = useMemo(() => {
    const geos: THREE.BufferGeometry[] = [];
    const types: string[] = [];
    for (const b of data.buildings) {
      geos.push(createBuildingGeometry(b.footprint, b.height, data.region.centerLng, data.region.centerLat));
      types.push(b.buildingType as string);
    }
    return { geometries: geos, buildingTypes: types };
  }, [data]);

  return (
    <group>
      {geometries.map((geo, i) => (
        <mesh
          key={i}
          geometry={geo}
          castShadow
          receiveShadow
        >
          <meshStandardMaterial
            color={BUILDING_COLORS[buildingTypes[i] ?? 'commercial'] ?? '#a1a1aa'}
            roughness={0.8}
          />
        </mesh>
      ))}
    </group>
  );
}

// ── Roads ─────────────────────────────────────────────────────────────────

const ROAD_COLORS: Record<string, string> = {
  arterial: '#52525b',
  residential: '#71717a',
  lane: '#a1a1aa'
};

const ROAD_WIDTHS: Record<string, number> = {
  arterial: 8,
  residential: 5,
  lane: 3
};

function Roads({ data }: { data: TwinSceneData }) {
  const segments = useMemo(() => {
    return data.roads.map((rd) => {
      const line = rd.line as unknown as GeoJsonLineString;
      const projected = projectLineString(line, data.region.centerLng, data.region.centerLat);
      if (projected.length < 2) return null;

      const [x1, z1] = projected[0]!;
      const [x2, z2] = projected[projected.length - 1]!;
      const midX = (x1! + x2!) / 2;
      const midZ = (z1! + z2!) / 2;
      const dx = x2! - x1!;
      const dz = z2! - z1!;
      const length = Math.sqrt(dx * dx + dz * dz);
      const angle = Math.atan2(dx, dz);
      const width = ROAD_WIDTHS[rd.roadType as string] ?? 5;

      return { midX, midZ, length, angle, width, roadType: rd.roadType as string };
    }).filter((s) => s !== null);
  }, [data]);

  return (
    <group>
      {segments.map((seg, i) => (
        <mesh
          key={i}
          position={[seg.midX, 0.05, seg.midZ]}
          rotation={[0, seg.angle, 0]}
          receiveShadow
        >
          <boxGeometry args={[seg.width, 0.2, seg.length]} />
          <meshStandardMaterial color={ROAD_COLORS[seg.roadType] ?? '#71717a'} roughness={1} />
        </mesh>
      ))}
    </group>
  );
}

// ── Asset markers ─────────────────────────────────────────────────────────

function AssetMarker({
  asset,
  centerLng,
  centerLat,
  onSelect,
  isSelected
}: {
  asset: TwinAsset;
  centerLng: number;
  centerLat: number;
  onSelect: (item: SelectedItem) => void;
  isSelected: boolean;
}) {
  const [x, z] = projectLngLat(asset.longitude, asset.latitude, centerLng, centerLat);
  const color = ASSET_TYPE_COLORS[asset.assetTypeSlug] ?? '#6366f1';
  const hasOpenIssues = asset.openIssueCount > 0;

  return (
    <group position={[x, 0, z]}>
      {/* Base pole */}
      <mesh position={[0, 1.5, 0]} castShadow>
        <cylinderGeometry args={[0.3, 0.3, 3, 8]} />
        <meshStandardMaterial
          color={color}
          emissive={isSelected ? color : '#000000'}
          emissiveIntensity={isSelected ? 0.5 : 0}
        />
      </mesh>
      {/* Top marker sphere */}
      <mesh
        position={[0, 3.5, 0]}
        castShadow
        onClick={(e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation();
          onSelect({ kind: 'asset', id: asset.id, data: asset });
        }}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => {
          e.stopPropagation();
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'default';
        }}
      >
        <sphereGeometry args={[0.6, 12, 12]} />
        <meshStandardMaterial
          color={color}
          emissive={hasOpenIssues ? '#ef4444' : color}
          emissiveIntensity={hasOpenIssues ? 0.6 : 0.2}
          roughness={0.4}
        />
      </mesh>
      {/* Open issue ring */}
      {hasOpenIssues && (
        <mesh position={[0, 0.15, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[1.2, 1.8, 24]} />
          <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.4} side={THREE.DoubleSide} />
        </mesh>
      )}
    </group>
  );
}

function AssetMarkers({
  data,
  onSelect,
  selectedId
}: {
  data: TwinSceneData;
  onSelect: (item: SelectedItem) => void;
  selectedId: string | null;
}) {
  return (
    <group>
      {data.assets.map((asset) => (
        <AssetMarker
          key={asset.id}
          asset={asset}
          centerLng={data.region.centerLng}
          centerLat={data.region.centerLat}
          onSelect={onSelect}
          isSelected={selectedId === asset.id}
        />
      ))}
    </group>
  );
}

// ── Issue beacons ─────────────────────────────────────────────────────────

function IssueBeacon({
  issue,
  centerLng,
  centerLat,
  onSelect,
  isSelected
}: {
  issue: TwinIssue;
  centerLng: number;
  centerLat: number;
  onSelect: (item: SelectedItem) => void;
  isSelected: boolean;
}) {
  const [x, z] = projectLngLat(issue.longitude, issue.latitude, centerLng, centerLat);
  const color = STATUS_COLORS[issue.status] ?? '#ef4444';
  const animRef = useRef<THREE.Mesh>(null);

  useFramePulse(animRef, isSelected);

  return (
    <group position={[x, 0, z]}>
      {/* Beacon cylinder */}
      <mesh
        ref={animRef}
        position={[0, 5, 0]}
        castShadow
        onClick={(e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation();
          onSelect({ kind: 'issue', id: issue.id, data: issue });
        }}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => {
          e.stopPropagation();
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'default';
        }}
      >
        <cylinderGeometry args={[0.4, 0.6, 2, 12]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.8}
          roughness={0.3}
        />
      </mesh>
      {/* Ground ring */}
      <mesh position={[0, 0.1, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1, 1.5, 24]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.5} side={THREE.DoubleSide} />
      </mesh>
      {/* Beam to sky */}
      <mesh position={[0, 15, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 20, 6]} />
        <meshBasicMaterial color={color} transparent opacity={0.3} />
      </mesh>
    </group>
  );
}

function useFramePulse(ref: RefObject<THREE.Mesh | null>, active: boolean) {
  useFrame((state) => {
    if (ref.current) {
      const t = state.clock.elapsedTime;
      ref.current.scale.y = active ? 1.2 + Math.sin(t * 4) * 0.15 : 1 + Math.sin(t * 2) * 0.05;
    }
  });
}

function IssueBeacons({
  data,
  onSelect,
  selectedId
}: {
  data: TwinSceneData;
  onSelect: (item: SelectedItem) => void;
  selectedId: string | null;
}) {
  return (
    <group>
      {data.issues.map((issue) => (
        <IssueBeacon
          key={issue.id}
          issue={issue}
          centerLng={data.region.centerLng}
          centerLat={data.region.centerLat}
          onSelect={onSelect}
          isSelected={selectedId === issue.id}
        />
      ))}
    </group>
  );
}

// ── Main scene ────────────────────────────────────────────────────────────

type OrbitControlsHandle = {
  object: THREE.Camera & { position: THREE.Vector3; matrixWorld: THREE.Matrix4 };
  target: THREE.Vector3;
  update: () => void;
  enabled: boolean;
};

function CivicCameraController({
  cameraRef,
  initialPosition,
  minDistance,
  maxDistance,
  bounds,
}: {
  cameraRef: ForwardedRef<TwinCameraController>;
  initialPosition: THREE.Vector3;
  minDistance: number;
  maxDistance: number;
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
}) {
  const controlsRef = useRef<OrbitControlsHandle | null>(null);
  const fromPosition = useRef(new THREE.Vector3());
  const fromTarget = useRef(new THREE.Vector3());
  const toPosition = useRef(initialPosition.clone());
  const toTarget = useRef(new THREE.Vector3());
  const progress = useRef(1);
  const animating = useRef(false);

  const moveTo = (position: THREE.Vector3, target: THREE.Vector3) => {
    const controls = controlsRef.current;
    if (!controls) return;
    fromPosition.current.copy(controls.object.position);
    fromTarget.current.copy(controls.target);
    toPosition.current.copy(position);
    toTarget.current.copy(target);
    progress.current = 0;
    animating.current = true;
    controls.enabled = false;
  };

  useImperativeHandle(cameraRef, () => ({
    zoomIn: () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const offset = controls.object.position.clone().sub(controls.target);
      const distance = THREE.MathUtils.clamp(offset.length() * 0.8, minDistance, maxDistance);
      moveTo(controls.target.clone().addScaledVector(offset.normalize(), distance), controls.target.clone());
    },
    zoomOut: () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const offset = controls.object.position.clone().sub(controls.target);
      const distance = THREE.MathUtils.clamp(offset.length() * 1.25, minDistance, maxDistance);
      moveTo(controls.target.clone().addScaledVector(offset.normalize(), distance), controls.target.clone());
    },
    pan: (direction) => {
      const controls = controlsRef.current;
      if (!controls) return;
      const distance = controls.object.position.distanceTo(controls.target);
      const forward = controls.target.clone().sub(controls.object.position).setY(0).normalize();
      const right = new THREE.Vector3().setFromMatrixColumn(controls.object.matrixWorld, 0).setY(0).normalize();
      const offset = direction === 'up' ? forward : direction === 'down' ? forward.negate() : direction === 'left' ? right.negate() : right;
      offset.multiplyScalar(Math.max(distance * 0.1, 8));
      const target = controls.target.clone().add(offset);
      target.x = THREE.MathUtils.clamp(target.x, bounds.minX, bounds.maxX);
      target.z = THREE.MathUtils.clamp(target.z, bounds.minZ, bounds.maxZ);
      moveTo(controls.object.position.clone().add(target.clone().sub(controls.target)), target);
    },
    reset: () => moveTo(initialPosition.clone(), new THREE.Vector3()),
  }));

  useFrame((_, delta) => {
    const controls = controlsRef.current;
    if (!controls || !animating.current) return;
    progress.current = Math.min(1, progress.current + delta * 3);
    const eased = 1 - Math.pow(1 - progress.current, 3);
    controls.object.position.lerpVectors(fromPosition.current, toPosition.current, eased);
    controls.target.lerpVectors(fromTarget.current, toTarget.current, eased);
    controls.update();
    if (progress.current === 1) {
      animating.current = false;
      controls.enabled = true;
    }
  });

  return <OrbitControls ref={controlsRef as never} enableDamping dampingFactor={0.1} maxPolarAngle={Math.PI / 2.2} minDistance={minDistance} maxDistance={maxDistance} target={[0, 0, 0]} />;
}

export const TwinScene = forwardRef<TwinCameraController, TwinSceneProps>(function TwinScene({
  data,
  onSelect,
  selectedId,
  children,
  showBoundary = true,
  showBuildings = true,
  showAssets = true,
  showIssues = true,
}, cameraRef) {
  const { camDistance, bounds } = useMemo(() => {
    const boundary = data.region.boundary as unknown as GeoJsonPolygon;
    const projected = projectPolygon(boundary, data.region.centerLng, data.region.centerLat);
    let maxDist = 0;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const [x, z] of projected) {
      const d = Math.sqrt(x! * x! + z! * z!);
      maxDist = Math.max(maxDist, d);
      minX = Math.min(minX, x!);
      maxX = Math.max(maxX, x!);
      minZ = Math.min(minZ, z!);
      maxZ = Math.max(maxZ, z!);
    }
    const camDistance = Math.max(maxDist * 1.8, 150);
    return {
      camDistance,
      bounds: Number.isFinite(minX)
        ? { minX, maxX, minZ, maxZ }
        : { minX: -camDistance, maxX: camDistance, minZ: -camDistance, maxZ: camDistance },
    };
  }, [data]);
  const initialPosition = useMemo(() => new THREE.Vector3(camDistance * 0.6, camDistance * 0.5, camDistance * 0.6), [camDistance]);

  return (
    <Canvas shadows camera={{ position: [initialPosition.x, initialPosition.y, initialPosition.z], fov: 50, near: 0.1, far: 5000 }} onPointerMissed={() => onSelect(null)}>
      <ambientLight intensity={0.6} />
      <directionalLight position={[200, 300, 100]} intensity={1.2} castShadow shadow-mapSize-width={2048} shadow-mapSize-height={2048} shadow-camera-left={-400} shadow-camera-right={400} shadow-camera-top={400} shadow-camera-bottom={-400} />
      <hemisphereLight args={['#dbeafe', '#4b5563', 0.4]} />
      <color attach="background" args={['#bfdbfe']} />
      {showBoundary && <RegionGround data={data} />}
      {showBuildings && <Buildings data={data} />}
      {showBoundary && <Roads data={data} />}
      {showAssets && <AssetMarkers data={data} onSelect={onSelect} selectedId={selectedId} />}
      {showIssues && <IssueBeacons data={data} onSelect={onSelect} selectedId={selectedId} />}
      {children}
      <CivicCameraController cameraRef={cameraRef} initialPosition={initialPosition} minDistance={20} maxDistance={camDistance * 2} bounds={bounds} />
    </Canvas>
  );
});

// ── WebGL detection ───────────────────────────────────────────────────────
// Delegates to the shared, cached pre-check that releases its throwaway GL
// context so repeated calls never leak concurrent contexts (iOS Safari cap).

export function isWebGLAvailable(): boolean {
  return checkWebGLCapability();
}

// ── Legend component ──────────────────────────────────────────────────────

export function TwinLegend(): ReactNode {
  const legend = (
    <div className="space-y-3 text-xs">
      <div>
        <p className="mb-1.5 font-semibold">Issue Status</p>
        <div className="space-y-1">
          {[
            { label: 'Submitted / Acknowledged', color: '#ef4444' },
            { label: 'In Progress / Scheduled', color: '#3b82f6' },
            { label: 'Resolved', color: '#22c55e' },
            { label: 'Closed / Rejected', color: '#64748b' },
          ].map((status) => (
            <div key={status.label} className="flex items-center gap-2">
              <span className="inline-block size-3 rounded-full" style={{ backgroundColor: status.color }} />
              <span className="text-muted-foreground">{status.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-1.5 font-semibold">Assets</p>
        <div className="space-y-1">
          {Object.entries(ASSET_TYPE_COLORS).slice(0, 6).map(([slug, color]) => (
            <div key={slug} className="flex items-center gap-2">
              <span className="inline-block size-3 rounded-full" style={{ backgroundColor: color }} />
              <span className="capitalize text-muted-foreground">{slug.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="absolute bottom-4 left-4 z-10 hidden max-w-[200px] rounded-lg border border-border bg-background/95 p-3 shadow-lg sm:block">
        {legend}
      </div>
      <Sheet>
        <div className="absolute bottom-4 left-4 z-10 sm:hidden" onPointerDown={(event) => event.stopPropagation()}>
          <SheetTrigger asChild>
            <Button type="button" size="icon" variant="secondary" className="size-11 rounded-xl border bg-card/95 shadow-lg backdrop-blur" aria-label="Open map legend">
              <ListIcon className="size-5" aria-hidden />
            </Button>
          </SheetTrigger>
        </div>
        <SheetContent side="bottom" className="max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:hidden" onPointerDown={(event) => event.stopPropagation()}>
          <SheetHeader>
            <SheetTitle>Map legend</SheetTitle>
            <SheetDescription>Issue status and asset markers in this digital twin.</SheetDescription>
          </SheetHeader>
          <div className="mt-5">{legend}</div>
        </SheetContent>
      </Sheet>
    </>
  );
}
